/**
 * Product Data Service
 * Version: 2.0.0
 *
 * Service layer for unified geogrid product data management.
 * Now fetches data from backend API instead of static import.
 * Provides backward-compatible API while supporting geolocation filtering.
 */

import {
    IUnifiedProductData,
    IUnifiedProduct,
    TGeogridProductData,
    IGeogridProductInfo,
    IGeogridFamily,
    IProductLookupResult,
    IProductFilterOptions,
    IProductFilterStats,
} from '../data/IUnifiedProductData';
import { logDebug, logWarn, logError } from '../utils/utils';

// Static fallback data for when API is unavailable
import STATIC_GEOGRID_DATA from '../data/geogrid-products-unified.json';

// API Base URL - use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASEURL || 'http://localhost:3004';

/**
 * ProductDataService - Singleton service for geogrid product data access
 *
 * IMPORTANT: Call ProductDataService.ensureInitialized() at app startup before using other methods.
 * This fetches data from the backend API once and caches it for synchronous access.
 *
 * Usage:
 * ```typescript
 * // At app startup (e.g., in App.tsx or index.tsx)
 * await ProductDataService.ensureInitialized();
 *
 * // Then use synchronously anywhere in the app
 * const products = ProductDataService.getAllProducts();
 *
 * // Get products filtered by country
 * const italyProducts = ProductDataService.getProductsByCountry('IT', allowedFamilies);
 *
 * // Get single product
 * const product = ProductDataService.getProductById('macgrid-wg-03s');
 * ```
 */
export class ProductDataService {
    /** Cached unified product data */
    private static data: IUnifiedProductData | null = null;

    /** Initialization flag */
    private static initialized = false;

    /** Initialization promise for concurrent calls */
    private static initPromise: Promise<void> | null = null;

    /** Cache TTL in milliseconds (1 hour) */
    private static readonly CACHE_TTL_MS = 60 * 60 * 1000;

    /** Cache timestamp */
    private static cacheTimestamp = 0;

    /**
     * Ensure the service is initialized by fetching data from the API.
     * This should be called at app startup.
     * Safe to call multiple times - will only fetch once (or re-fetch if cache is stale).
     */
    public static async ensureInitialized(): Promise<void> {
        // If already initialized and cache is fresh, return immediately
        if (this.initialized && this.data && this.isCacheFresh()) {
            return;
        }

        // If initialization is in progress, wait for it
        if (this.initPromise) {
            return this.initPromise;
        }

        // Start initialization
        this.initPromise = this.fetchAndCacheData();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * Check if cache is still fresh
     */
    private static isCacheFresh(): boolean {
        return Date.now() - this.cacheTimestamp < this.CACHE_TTL_MS;
    }

    /**
     * Fetch data from API and cache it
     */
    private static async fetchAndCacheData(): Promise<void> {
        try {
            logDebug('ProductDataService', `Fetching geogrid products from API: ${API_BASE_URL}/api/data/products/geogrid`);

            const response = await fetch(`${API_BASE_URL}/api/data/products/geogrid`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store',
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }

            const data: IUnifiedProductData = await response.json();

            this.data = data;
            this.cacheTimestamp = Date.now();
            this.initialized = true;

            logDebug(
                'ProductDataService',
                `Initialized with ${this.data.metadata.totalProducts} products across ${this.data.metadata.totalFamilies} families`,
                {
                    version: this.data.version,
                    lastUpdated: this.data.lastUpdated,
                }
            );
        } catch (error) {
            logError('ProductDataService', 'Failed to fetch product data from API', error);

            // If we have stale cached data, keep using it
            if (this.data) {
                logWarn('ProductDataService', 'Using stale cached data due to API fetch failure');
                return;
            }

            // Fallback to static data when API is unavailable and no cached data exists
            logWarn('ProductDataService', 'Using static fallback data - API unavailable');
            this.data = STATIC_GEOGRID_DATA as unknown as IUnifiedProductData;
            this.cacheTimestamp = Date.now();
            this.initialized = true;
        }
    }

    /**
     * Initialize the service synchronously (legacy method for backward compatibility)
     * Will throw if data hasn't been fetched yet. Call ensureInitialized() first.
     */
    private static init(): void {
        if (!this.initialized || !this.data) {
            // Try to use cached data if available, even if stale
            if (this.data) {
                logWarn('ProductDataService', 'Using cached data - call ensureInitialized() for fresh data');
                return;
            }

            throw new Error(
                'ProductDataService not initialized. Call ProductDataService.ensureInitialized() at app startup.'
            );
        }
    }

    /**
     * Get all products in the original format (backward compatible)
     * Returns: Record<FamilyName, Product[]>
     *
     * @returns TGeogridProductData in the original format
     */
    public static getAllProducts(): TGeogridProductData {
        this.init();
        if (!this.data) {
            throw new Error('Product data not loaded');
        }

        // Convert IUnifiedProduct[] to IGeogridProductInfo[] for each family
        const result: TGeogridProductData = {};

        for (const [familyName, products] of Object.entries(this.data.products)) {
            result[familyName] = products.map((product) => this.toGeogridProductInfo(product));
        }

        return result;
    }

    /**
     * Get products filtered by country and allowed families
     *
     * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "IT")
     * @param allowedFamilies - Families allowed for this country (from backend)
     * @returns Filtered products in original format
     */
    public static getProductsByCountry(countryCode: string, allowedFamilies: IGeogridFamily[]): TGeogridProductData {
        this.init();
        if (!this.data) {
            throw new Error('Product data not loaded');
        }

        logDebug('ProductDataService', `Filtering products for country: ${countryCode}`, {
            allowedFamilies: allowedFamilies.map((f) => f.name),
        });

        const filtered: TGeogridProductData = {};

        for (const [familyName, products] of Object.entries(this.data.products)) {
            // Strategy 1: Check if family is in allowed list (exact match)
            const familyMatch = allowedFamilies.find((f) => f.name === familyName);

            if (familyMatch) {
                // Family is allowed, now filter products by region restrictions
                const regionFilteredProducts = products.filter((product) => {
                    // If applicableRegions is null or undefined, product is global (available everywhere)
                    if (!product.applicableRegions) return true;

                    // If applicableRegions is empty array, product is discontinued
                    if (product.applicableRegions.length === 0) {
                        logWarn('ProductDataService', `Product ${product.prodotto} has empty applicableRegions (discontinued)`, {
                            productId: product.id,
                        });
                        return false;
                    }

                    // Product has specific region restrictions
                    const isAvailableInCountry = product.applicableRegions.includes(countryCode);
                    if (!isAvailableInCountry) {
                        logDebug('ProductDataService', `Product ${product.prodotto} not available in ${countryCode}`, {
                            productId: product.id,
                            availableIn: product.applicableRegions,
                        });
                    }
                    return isAvailableInCountry;
                });

                if (regionFilteredProducts.length > 0) {
                    filtered[familyName] = regionFilteredProducts.map((p) => this.toGeogridProductInfo(p));
                    logDebug('ProductDataService', `Family "${familyName}" included with ${regionFilteredProducts.length} products`);
                } else {
                    logDebug('ProductDataService', `Family "${familyName}" excluded: no products available in ${countryCode}`);
                }

                continue;
            }

            // Strategy 2: Substring matching (product name contains family name)
            // This handles cases where backend returns family name differently than we store it
            const matchingProducts = products.filter((product) => {
                const productMatches = allowedFamilies.some((family) =>
                    product.prodotto.toUpperCase().includes(family.name.toUpperCase())
                );

                if (productMatches) {
                    // Also check region restrictions
                    if (!product.applicableRegions) return true;
                    if (product.applicableRegions.length === 0) return false;
                    return product.applicableRegions.includes(countryCode);
                }

                return false;
            });

            if (matchingProducts.length > 0) {
                filtered[familyName] = matchingProducts.map((p) => this.toGeogridProductInfo(p));
                logDebug(
                    'ProductDataService',
                    `Family "${familyName}" matched by substring, including ${matchingProducts.length}/${products.length} products`
                );
            }
        }

        const stats = this.getFilterStats(filtered);
        logDebug('ProductDataService', 'Filtering complete', stats);

        return filtered;
    }

    /**
     * Get families available for a specific country
     *
     * @param countryCode - ISO 3166-1 alpha-2 country code
     * @param allowedFamilies - Families from backend
     * @returns Array of family names available for this country
     */
    public static getFamiliesForCountry(countryCode: string, allowedFamilies: IGeogridFamily[]): string[] {
        const products = this.getProductsByCountry(countryCode, allowedFamilies);
        return Object.keys(products);
    }

    /**
     * Get a single product by its ID
     *
     * @param productId - Product ID (e.g., "macgrid-wg-03s")
     * @returns Product lookup result
     */
    public static getProductById(productId: string): IProductLookupResult {
        this.init();
        if (!this.data) {
            throw new Error('Product data not loaded');
        }

        for (const [familyName, products] of Object.entries(this.data.products)) {
            const product = products.find((p) => p.id === productId);
            if (product) {
                return { product, family: familyName };
            }
        }

        logWarn('ProductDataService', `Product not found: ${productId}`);
        return { product: null, family: null };
    }

    /**
     * Get products with advanced filtering options
     *
     * @param options - Filter options
     * @returns Filtered products
     */
    public static getProductsWithFilters(options: IProductFilterOptions): TGeogridProductData {
        this.init();
        if (!this.data) {
            throw new Error('Product data not loaded');
        }

        let result: TGeogridProductData = {};

        // Start with all products or filtered by family
        if (options.familyName) {
            const products = this.data.products[options.familyName];
            if (products) {
                result[options.familyName] = products.map((p) => this.toGeogridProductInfo(p));
            }
        } else if (options.familyId) {
            // Find family by ID
            for (const [familyName, products] of Object.entries(this.data.products)) {
                if (products.length > 0 && products[0].familyId === options.familyId) {
                    result[familyName] = products.map((p) => this.toGeogridProductInfo(p));
                    break;
                }
            }
        } else {
            result = this.getAllProducts();
        }

        // Apply additional filters
        const filtered: TGeogridProductData = {};

        for (const [familyName] of Object.entries(result)) {
            const unifiedProducts = this.data.products[familyName];
            if (!unifiedProducts) continue;

            const filteredProducts = unifiedProducts.filter((product) => {
                // Filter by country
                if (options.countryCode) {
                    if (product.applicableRegions && product.applicableRegions.length > 0) {
                        if (!product.applicableRegions.includes(options.countryCode)) {
                            return false;
                        }
                    }
                }

                // Filter by project type
                // Products must have applicableProjects defined and non-empty to be included
                if (options.projectType) {
                    if (!product.applicableProjects || product.applicableProjects.length === 0) {
                        return false; // Exclude products without specific project type configuration
                    }
                    if (!product.applicableProjects.includes(options.projectType)) {
                        return false;
                    }
                }

                // Filter by category (GEOGRID or GEOTEXTILE)
                if (options.category) {
                    if (product.category !== options.category) {
                        return false;
                    }
                }

                // Filter by complete technical data
                if (options.onlyCompleteTechnicalData) {
                    if (
                        product.tensile_1_percent === null ||
                        product.tensile_2_percent === null ||
                        product.tensile_3_percent === null ||
                        product.tensile_4_percent === null ||
                        product.tensile_5_percent === null
                    ) {
                        return false;
                    }
                }

                return true;
            });

            if (filteredProducts.length > 0) {
                filtered[familyName] = filteredProducts.map((p) => this.toGeogridProductInfo(p));
            }
        }

        return filtered;
    }

    /**
     * Get database metadata
     */
    public static getMetadata(): { version: string; lastUpdated: string; totalProducts: number; totalFamilies: number } {
        this.init();
        if (!this.data) {
            throw new Error('Product data not loaded');
        }

        return {
            version: this.data.version,
            lastUpdated: this.data.lastUpdated,
            totalProducts: this.data.metadata.totalProducts,
            totalFamilies: this.data.metadata.totalFamilies,
        };
    }

    /**
     * Check if the service is initialized
     */
    public static isInitialized(): boolean {
        return this.initialized && this.data !== null;
    }

    /**
     * Get all products as raw IUnifiedProduct objects (preserving LCR fields).
     * Used by GeogridLcrBridge to build LCR equation lookup tables.
     * Returns empty record if data is not loaded.
     */
    public static getAllUnifiedProducts(): Record<string, IUnifiedProduct[]> {
        if (!this.data) {
            return {};
        }
        return this.data.products;
    }

    /**
     * Get statistics about filtered products
     */
    private static getFilterStats(products: TGeogridProductData): IProductFilterStats {
        const families = Object.keys(products);
        const familyBreakdown: Record<string, number> = {};
        let totalProducts = 0;

        for (const [family, prods] of Object.entries(products)) {
            familyBreakdown[family] = prods.length;
            totalProducts += prods.length;
        }

        return {
            totalFamilies: families.length,
            totalProducts,
            families,
            familyBreakdown,
        };
    }

    /**
     * Convert IUnifiedProduct to IGeogridProductInfo (backward compatibility)
     */
    private static toGeogridProductInfo(product: IUnifiedProduct): IGeogridProductInfo {
        return {
            prodotto: product.prodotto,
            tensile_max: product.tensile_max,
            tensile: product.tensile,
            tensile_1_percent: product.tensile_1_percent,
            tensile_2_percent: product.tensile_2_percent,
            tensile_3_percent: product.tensile_3_percent,
            tensile_4_percent: product.tensile_4_percent,
            tensile_5_percent: product.tensile_5_percent,
            torsional_stiffness: product.torsional_stiffness,
        };
    }

    /**
     * Reset service (for testing purposes)
     */
    public static reset(): void {
        this.data = null;
        this.initialized = false;
        this.initPromise = null;
        this.cacheTimestamp = 0;
    }

    /**
     * Set test data directly (for testing purposes only)
     * This allows tests to inject mock data without making API calls
     */
    public static setTestData(testData: IUnifiedProductData): void {
        this.data = testData;
        this.initialized = true;
        this.cacheTimestamp = Date.now();
    }

    /**
     * Force refresh data from API (bypasses cache)
     */
    public static async forceRefresh(): Promise<void> {
        this.cacheTimestamp = 0; // Invalidate cache
        await this.ensureInitialized();
    }
}
